import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import CurrencyInput from "react-currency-input-field";
import { CURRENCY_SYMBOL_MAP } from "../utils/constants";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave, faPiggyBank } from "@fortawesome/free-solid-svg-icons";

function BatchUpdateSavingBalanceForm({ savingAccounts, onSavingUpdateSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [balances, setBalances] = useState({});
  const [note, setNote] = useState("");
  const { user } = useAuth();
  const { t, i18n } = useTranslation(); // Destructure i18n here

  useEffect(() => {
    if (savingAccounts && savingAccounts.length > 0) {
      const initialBalances = {};
      savingAccounts.forEach((account) => {
        initialBalances[account.id] = account.balance;
      });
      setBalances(initialBalances);
    }
  }, [savingAccounts]);

  const handleBalanceChange = (accountId, value) => {
    setBalances((prevBalances) => ({
      ...prevBalances,
      [accountId]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const updates = Object.keys(balances).map((accountId) => ({
      id: accountId,
      balance: parseFloat(balances[accountId]) || 0,
    }));

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/saving-accounts/batch-update-balance`, {
        updates,
        note,
        reporter: user.name.split(" ")[0].charAt(0).toUpperCase() + user.name.split(" ")[0].slice(1),
        locale: i18n.language,
      });

      if (onSavingUpdateSuccess) {
        onSavingUpdateSuccess();
      }
      setNote(""); // Clear the note after successful submission
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card bg-white dark:bg-dark-surface p-3 transition-colors">
      <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-6 flex items-center gap-2">
        <FontAwesomeIcon icon={faPiggyBank} className="text-primary-500" />
        {t("updateAllSavingBalances")}
      </h2>

      {error && (
        <div className="text-red-500 mb-4 bg-red-50 p-3 rounded-lg text-center">
          {t("error")}: {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {!savingAccounts || savingAccounts.length === 0 ? (
          <p className="text-gray-600 dark:text-gray-400 text-center">{t("noSavingAccountsFound")}</p>
        ) : (
          <div className="space-y-4">
            {savingAccounts
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((account) => (
                <div
                  key={account.id}
                  className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors gap-3"
                >
                  <label htmlFor={`balance-${account.id}`} className="text-base font-medium text-gray-700 dark:text-gray-300 sm:w-1/2">
                    {account.name}
                    <span className="text-xs text-gray-500 dark:text-gray-400 block sm:inline sm:ml-2 font-normal">
                      ({account.holder})
                    </span>
                  </label>
                  <CurrencyInput
                    id={`balance-${account.id}`}
                    name={`balance-${account.id}`}
                    value={balances[account.id] !== undefined ? balances[account.id] : ""}
                    onValueChange={(value) => handleBalanceChange(account.id, value)}
                    className="w-full sm:w-1/2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-3 text-right font-bold text-gray-800 dark:text-white focus:ring-2 focus:ring-primary-100 outline-none transition-all"
                    placeholder={t("enterNewBalance")}
                    decimalsLimit={2}
                    suffix={` ${CURRENCY_SYMBOL_MAP[account.currency]}`}
                  />
                </div>
              ))}
          </div>
        )}

        <div className="mt-8">
          <label htmlFor="note" className="block text-lg font-bold text-gray-700 dark:text-gray-300 mb-2">
            {t("note")}
          </label>
          <textarea
            id="note"
            rows="3"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="w-full p-4 bg-gray-50 dark:bg-gray-800 border-none rounded-xl focus:ring-2 focus:ring-primary-100 outline-none transition-all resize-none dark:text-white"
            placeholder={t("addANote")}
            required
          ></textarea>
        </div>

        <button
          type="submit"
          className="btn-primary w-full mt-6 py-4 rounded-xl flex items-center justify-center gap-2 font-bold text-lg shadow-lg shadow-primary-500/30"
          disabled={loading}
        >
          {loading ? (
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
          ) : (
            <>
              <FontAwesomeIcon icon={faSave} />
              {t("updateAllSavingBalances")}
            </>
          )}
        </button>
      </form>
    </div>
  );
}

export default BatchUpdateSavingBalanceForm;
